import { PASSWORD_MIN_LENGTH, TOTP_CODE_DIGITS } from '@bartizo/shared';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { isApiError } from '../api/client.js';
import { useSession } from '../auth/session-context.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { TextField } from '../ui/Field.js';
import { ShieldIcon } from '../ui/icons.js';

/**
 * Sign in (knowledge doc section 17; section 20 chose new authentication built to
 * these three roles rather than reusing another project's).
 *
 * Two steps when the account has an authenticator, and the second only appears
 * once the API has asked for it. The alternative — showing a code box to everyone —
 * asks most people for something they do not have, and asking for a password and a
 * code in one breath tells an attacker nothing either way about which was wrong.
 *
 * The form says as little as it can about why a sign-in failed, because the API
 * deliberately does not distinguish an unknown address from a wrong password, and a
 * screen that guessed at the difference would undo that.
 */

type Stage = 'credentials' | 'code';

export function LoginPage(): ReactNode {
  const { signIn, loadError } = useSession();
  const [stage, setStage] = useState<Stage>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password, stage === 'code' ? code : undefined);
      setPassword('');
      setCode('');
    } catch (caught: unknown) {
      if (isApiError(caught) && caught.code === 'totp_required') {
        // The password was right. Ask for the code and keep it out of the way of
        // the error slot, which is for things that went wrong.
        setStage('code');
        setError(null);
      } else {
        setError(isApiError(caught) ? caught.message : 'Could not reach the Bartizo API.');
        if (isApiError(caught) && caught.code === 'invalid_credentials') setStage('credentials');
      }
      setBusy(false);
    }
  };

  return (
    <main className="centred">
      <div className="card" style={{ width: 'min(24rem, 100%)' }}>
        <div className="card-body">
          <div className="stack" style={{ gap: 'var(--space-1)', marginBottom: 'var(--space-2)' }}>
            <ShieldIcon style={{ width: '2rem', height: '2rem', color: 'var(--accent)' }} />
            <h1>Bartizo</h1>
            <p className="muted text-sm">
              {stage === 'credentials'
                ? 'Sign in to the fleet console'
                : 'One more step to confirm it is you'}
            </p>
          </div>

          {loadError !== null && (
            <Alert tone="warning" title="The API could not be reached">
              Signing in may not work until it is back.
            </Alert>
          )}

          <form onSubmit={(event) => void submit(event)} className="stack">
            {stage === 'credentials' ? (
              <>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />
                <TextField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                />
              </>
            ) : (
              <TextField
                label="Authenticator code"
                hint={`The ${String(TOTP_CODE_DIGITS)}-digit code from your authenticator app.`}
                // `one-time-code` is what lets a phone offer the code from the
                // keyboard rather than making someone switch apps to read it.
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={TOTP_CODE_DIGITS}
                autoFocus
                required
                value={code}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, ''));
                }}
              />
            )}

            {error !== null && <Alert tone="danger">{error}</Alert>}

            <Button type="submit" variant="primary" block busy={busy} busyLabel="Signing in…">
              {stage === 'credentials' ? 'Sign in' : 'Confirm'}
            </Button>

            {stage === 'code' && (
              <Button
                variant="ghost"
                block
                onClick={() => {
                  setStage('credentials');
                  setCode('');
                  setError(null);
                }}
              >
                Use a different account
              </Button>
            )}
          </form>
        </div>
      </div>
    </main>
  );
}
